import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { useCustomerAuth } from "../contexts/CustomerAuthContext";
import { API } from "../config";
import { User, Package, MapPin, Heart, LogOut, Edit, Save, Trash2, Plus, Lock, ChevronRight, Mail, AlertCircle, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const NIGERIAN_STATES = [
  "Abia","Adamawa","Akwa Ibom","Anambra","Bauchi","Bayelsa","Benue","Borno",
  "Cross River","Delta","Ebonyi","Edo","Ekiti","Enugu","FCT","Gombe","Imo",
  "Jigawa","Kaduna","Kano","Katsina","Kebbi","Kogi","Kwara","Lagos","Nasarawa",
  "Niger","Ogun","Ondo","Osun","Oyo","Plateau","Rivers","Sokoto","Taraba","Yobe","Zamfara"
];

const formatPrice = (price) => `₦${price?.toLocaleString() || 0}`;

const ProfileTab = ({ customer, customerAxios }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: customer?.name || "", phone: customer?.phone || "" });
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "" });

  const saveProfile = async () => {
    try {
      await customerAxios.put("/customer/profile", form);
      toast.success("Profile updated!");
      setEditing(false);
    } catch (err) { toast.error(err.response?.data?.detail || "Error updating profile"); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwForm.new_password.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    try {
      await customerAxios.post("/customer/change-password", pwForm);
      toast.success("Password changed!");
      setPwForm({ current_password: "", new_password: "" });
    } catch (err) { toast.error(err.response?.data?.detail || "Error changing password"); }
  };

  return (
    <div className="space-y-6" data-testid="profile-tab">
      <div className="border-2 border-[#0A0A0A] p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-black uppercase">Personal Info</h3>
          <Button onClick={() => editing ? saveProfile() : setEditing(true)} className={editing ? "btn-primary" : "btn-outline"} size="sm">
            {editing ? <><Save className="w-4 h-4 mr-1" /> Save</> : <><Edit className="w-4 h-4 mr-1" /> Edit</>}
          </Button>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm text-gray-500">Name</Label>
            {editing ? <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="profile-name-input" />
              : <p className="font-bold">{customer?.name}</p>}
          </div>
          <div>
            <Label className="text-sm text-gray-500">Email</Label>
            <p className="font-bold">{customer?.email}</p>
          </div>
          <div>
            <Label className="text-sm text-gray-500">Phone</Label>
            {editing ? <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" data-testid="profile-phone-input" />
              : <p className="font-bold">{customer?.phone || "Not set"}</p>}
          </div>
          <div>
            <Label className="text-sm text-gray-500">Account Type</Label>
            <p className="font-bold capitalize">{customer?.auth_provider || "Email"}</p>
          </div>
        </div>
      </div>

      {customer?.auth_provider !== "google" && (
        <div className="border-2 border-[#0A0A0A] p-6">
          <h3 className="font-black uppercase mb-4 flex items-center gap-2"><Lock className="w-4 h-4" /> Change Password</h3>
          <form onSubmit={changePassword} className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm">Current Password</Label>
              <Input type="password" value={pwForm.current_password} onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })} required className="mt-1" data-testid="current-password-input" />
            </div>
            <div>
              <Label className="text-sm">New Password</Label>
              <Input type="password" value={pwForm.new_password} onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} required className="mt-1" data-testid="new-password-input" />
            </div>
            <Button type="submit" className="btn-primary sm:col-span-2" data-testid="change-password-button">Change Password</Button>
          </form>
        </div>
      )}
    </div>
  );
};

const OrdersTab = ({ customerAxios }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    customerAxios.get("/customer/orders")
      .then((res) => setOrders(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [customerAxios]);

  if (loading) return <div className="flex justify-center py-12"><div className="spinner" /></div>;
  if (orders.length === 0) return (
    <div className="text-center py-16 border-2 border-[#E5E5E5]" data-testid="no-orders">
      <Package className="w-16 h-16 mx-auto text-gray-300 mb-4" />
      <h3 className="text-xl font-bold mb-2">No orders yet</h3>
      <p className="text-gray-500 mb-4">Start shopping to see your orders here</p>
      <Link to="/"><Button className="btn-primary">Shop Now</Button></Link>
    </div>
  );

  const statusColor = { pending: "bg-yellow-100 text-yellow-800", confirmed: "bg-blue-100 text-blue-800", processing: "bg-purple-100 text-purple-800", shipped: "bg-indigo-100 text-indigo-800", delivered: "bg-green-100 text-green-800", cancelled: "bg-red-100 text-red-800" };

  return (
    <div className="space-y-4" data-testid="orders-tab">
      {orders.map((order) => (
        <div key={order.id} className="border-2 border-[#0A0A0A] p-4 sm:p-6" data-testid={`order-${order.id}`}>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
            <div>
              <p className="font-black text-sm">{order.order_number}</p>
              <p className="text-xs text-gray-500">{new Date(order.created_at).toLocaleDateString()}</p>
            </div>
            <div className="flex items-center gap-3">
              <span className={`px-3 py-1 text-xs font-bold uppercase rounded-full ${statusColor[order.status] || "bg-gray-100"}`}>{order.status}</span>
              <span className="font-black text-[#FF6B00]">{formatPrice(order.total)}</span>
            </div>
          </div>
          <div className="space-y-2">
            {order.items.map((item, idx) => (
              <div key={`${item.product_id}-${item.selected_size || idx}`} className="flex items-center gap-3 text-sm">
                {item.image && <img src={item.image} alt="" className="w-10 h-10 object-cover border" />}
                <span className="flex-1">{item.name}{item.selected_size ? ` (${item.selected_size})` : ""}</span>
                <span className="text-gray-500">x{item.quantity}</span>
                <span className="font-bold">{formatPrice(item.total)}</span>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t border-[#E5E5E5] flex justify-end">
            <Link to={`/track?order=${order.order_number}`} className="text-[#FF6B00] text-sm font-bold hover:underline flex items-center gap-1">
              Track Order <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      ))}
    </div>
  );
};

const AddressesTab = ({ customerAxios }) => {
  const [addresses, setAddresses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ label: "Home", full_name: "", phone: "", address_line1: "", address_line2: "", city: "", state: "", postal_code: "", is_default: false });

  const fetchAddresses = useCallback(() => {
    customerAxios.get("/customer/addresses").then((res) => setAddresses(res.data)).catch(console.error);
  }, [customerAxios]);

  useEffect(() => { fetchAddresses(); }, [fetchAddresses]);

  const saveAddress = async (e) => {
    e.preventDefault();
    try {
      const res = await customerAxios.post("/customer/addresses", form);
      setAddresses(res.data);
      setShowForm(false);
      setForm({ label: "Home", full_name: "", phone: "", address_line1: "", address_line2: "", city: "", state: "", postal_code: "", is_default: false });
      toast.success("Address saved!");
    } catch (err) { toast.error("Error saving address"); }
  };

  const deleteAddress = async (id) => {
    const res = await customerAxios.delete(`/customer/addresses/${id}`);
    setAddresses(res.data);
    toast.success("Address removed");
  };

  return (
    <div data-testid="addresses-tab">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-black uppercase">Saved Addresses</h3>
        <Button onClick={() => setShowForm(!showForm)} className="btn-primary" size="sm" data-testid="add-address-button">
          <Plus className="w-4 h-4 mr-1" /> Add Address
        </Button>
      </div>

      {showForm && (
        <form onSubmit={saveAddress} className="border-2 border-[#FF6B00] p-6 mb-6 space-y-4" data-testid="address-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-bold">Label</Label>
              <Select value={form.label} onValueChange={(v) => setForm({ ...form, label: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Home">Home</SelectItem>
                  <SelectItem value="Office">Office</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-bold">Full Name</Label>
              <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required className="mt-1" />
            </div>
            <div>
              <Label className="text-sm font-bold">Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required className="mt-1" />
            </div>
            <div>
              <Label className="text-sm font-bold">Address Line 1</Label>
              <Input value={form.address_line1} onChange={(e) => setForm({ ...form, address_line1: e.target.value })} required className="mt-1" />
            </div>
            <div>
              <Label className="text-sm font-bold">City</Label>
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} required className="mt-1" />
            </div>
            <div>
              <Label className="text-sm font-bold">State</Label>
              <Select value={form.state} onValueChange={(v) => setForm({ ...form, state: v })}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select state" /></SelectTrigger>
                <SelectContent>
                  {NIGERIAN_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex gap-2">
            <Button type="button" onClick={() => setShowForm(false)} className="btn-outline">Cancel</Button>
            <Button type="submit" className="btn-primary" data-testid="save-address-button">Save Address</Button>
          </div>
        </form>
      )}

      {addresses.length === 0 && !showForm ? (
        <div className="text-center py-12 border-2 border-[#E5E5E5]">
          <MapPin className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500">No saved addresses</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {addresses.map((addr) => (
            <div key={addr.id} className="border-2 border-[#0A0A0A] p-4 relative">
              <span className="text-xs font-bold bg-[#FF6B00] text-white px-2 py-0.5 absolute top-2 right-2">{addr.label}</span>
              <p className="font-bold">{addr.full_name}</p>
              <p className="text-sm text-gray-600">{addr.phone}</p>
              <p className="text-sm text-gray-600">{addr.address_line1}</p>
              <p className="text-sm text-gray-600">{addr.city}, {addr.state}</p>
              <button onClick={() => deleteAddress(addr.id)} className="text-red-500 text-xs mt-2 flex items-center gap-1 hover:underline">
                <Trash2 className="w-3 h-3" /> Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const WishlistTab = ({ customerAxios }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchWishlist = useCallback(() => {
    setLoading(true);
    customerAxios.get("/customer/wishlist")
      .then((res) => setItems(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [customerAxios]);

  useEffect(() => { fetchWishlist(); }, [fetchWishlist]);

  const removeItem = async (productId) => {
    await customerAxios.delete(`/customer/wishlist/${productId}`);
    setItems((prev) => prev.filter((p) => p.id !== productId));
    toast.success("Removed from wishlist");
  };

  if (loading) return <div className="flex justify-center py-12"><div className="spinner" /></div>;
  if (items.length === 0) return (
    <div className="text-center py-16 border-2 border-[#E5E5E5]" data-testid="empty-wishlist">
      <Heart className="w-16 h-16 mx-auto text-gray-300 mb-4" />
      <h3 className="text-xl font-bold mb-2">Wishlist is empty</h3>
      <p className="text-gray-500 mb-4">Save items you love for later</p>
      <Link to="/"><Button className="btn-primary">Browse Products</Button></Link>
    </div>
  );

  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="wishlist-tab">
      {items.map((product) => (
        <div key={product.id} className="border-2 border-[#0A0A0A] group">
          <Link to={`/product/${product.slug}`}>
            <div className="aspect-square bg-[#F5F5F5]">
              <img src={product.images?.[0] || "/placeholder.jpg"} alt={product.name} className="w-full h-full object-cover" />
            </div>
          </Link>
          <div className="p-4">
            <Link to={`/product/${product.slug}`} className="font-bold text-sm hover:text-[#FF6B00] line-clamp-1">{product.name}</Link>
            <p className="font-black text-[#FF6B00] mt-1">{formatPrice(product.selling_price)}</p>
            <button onClick={() => removeItem(product.id)} className="text-red-500 text-xs mt-2 flex items-center gap-1 hover:underline">
              <Trash2 className="w-3 h-3" /> Remove
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default function CustomerAccountPage() {
  const { customer, loading, logoutCustomer, customerAxios } = useCustomerAuth();
  const navigate = useNavigate();
  const [sendingVerification, setSendingVerification] = useState(false);

  useEffect(() => {
    if (!loading && !customer) navigate("/login");
  }, [loading, customer, navigate]);

  const sendVerification = async () => {
    setSendingVerification(true);
    try {
      const res = await customerAxios.post("/customer/send-verification");
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send verification email");
    }
    setSendingVerification(false);
  };

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="spinner" /></div>;
  if (!customer) return null;

  return (
    <div className="py-8 md:py-12" data-testid="customer-account-page">
      <div className="container-main">
        {/* Email verification banner */}
        {!customer.email_verified && customer.auth_provider !== "google" && (
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-amber-50 border-2 border-amber-400 p-4 mb-6" data-testid="verify-email-banner">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <div>
                <p className="font-bold text-amber-800 text-sm">Email not verified</p>
                <p className="text-amber-700 text-xs">Verify your email to unlock checkout. Check your inbox for a verification link.</p>
              </div>
            </div>
            <Button onClick={sendVerification} disabled={sendingVerification} className="bg-amber-500 hover:bg-amber-600 text-white text-sm flex-shrink-0" size="sm" data-testid="resend-verification-button">
              <Mail className="w-4 h-4 mr-1" /> {sendingVerification ? "Sending..." : "Resend Link"}
            </Button>
          </div>
        )}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-black uppercase tracking-tight">My Account</h1>
            <p className="text-gray-500">Welcome back, {customer.name}</p>
          </div>
          <Button onClick={() => { logoutCustomer(); navigate("/"); }} className="btn-outline flex items-center gap-2" data-testid="customer-logout-button">
            <LogOut className="w-4 h-4" /> Sign Out
          </Button>
        </div>

        <Tabs defaultValue="orders">
          <TabsList className="w-full justify-start border-b-2 border-[#E5E5E5] bg-transparent rounded-none gap-0 h-auto p-0">
            <TabsTrigger value="orders" className="data-[state=active]:border-b-2 data-[state=active]:border-[#FF6B00] rounded-none px-6 py-3 font-bold" data-testid="orders-tab-trigger">
              <Package className="w-4 h-4 mr-2" /> Orders
            </TabsTrigger>
            <TabsTrigger value="addresses" className="data-[state=active]:border-b-2 data-[state=active]:border-[#FF6B00] rounded-none px-6 py-3 font-bold" data-testid="addresses-tab-trigger">
              <MapPin className="w-4 h-4 mr-2" /> Addresses
            </TabsTrigger>
            <TabsTrigger value="wishlist" className="data-[state=active]:border-b-2 data-[state=active]:border-[#FF6B00] rounded-none px-6 py-3 font-bold" data-testid="wishlist-tab-trigger">
              <Heart className="w-4 h-4 mr-2" /> Wishlist
            </TabsTrigger>
            <TabsTrigger value="profile" className="data-[state=active]:border-b-2 data-[state=active]:border-[#FF6B00] rounded-none px-6 py-3 font-bold" data-testid="profile-tab-trigger">
              <User className="w-4 h-4 mr-2" /> Profile
            </TabsTrigger>
          </TabsList>
          <div className="mt-6">
            <TabsContent value="orders"><OrdersTab customerAxios={customerAxios} /></TabsContent>
            <TabsContent value="addresses"><AddressesTab customerAxios={customerAxios} /></TabsContent>
            <TabsContent value="wishlist"><WishlistTab customerAxios={customerAxios} /></TabsContent>
            <TabsContent value="profile"><ProfileTab customer={customer} customerAxios={customerAxios} /></TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  );
}
